import React, { useState } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Link, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid2, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const currencyOptions = [
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
  { value: 'JPY', label: 'Japanese Yen (JPY)' },
  { value: 'CAD', label: 'Canadian Dollar (CAD)' },
  { value: 'AUD', label: 'Australian Dollar (AUD)' },
  { value: 'INR', label: 'Indian Rupee (INR)' },
  { value: 'PLN', label: 'Polish Zloty (PLN)' },
];

const currencySymbols = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: '$',
  AUD: '$',
  INR: '₹',
  PLN: 'zł'
};

const csvHeaders = [
  { label: 'Description', key: 'description' },
  { label: 'Link', key: 'link' },
  { label: 'Login', key: 'login' },
  { label: 'Password', key: 'password' },
  { label: 'Amount', key: 'amount' },
  { label: 'Payment Due Date', key: 'paymentDueDate' }
];

// Calculate the closest upcoming due date for a given day of the month
const getClosestDueDate = (dueDay) => {
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
  
    let dueMonth = currentMonth;
    let dueYear = currentYear;
  
    // If due day has passed this month or is today, move to next month
    if (currentDay >= dueDay) {
      dueMonth += 1;
      if (dueMonth > 11) {
        dueMonth = 0;
        dueYear += 1;
      }
    }
  
    // Create due date, adjust if invalid (e.g., Feb 31)
    let dueDate = new Date(dueYear, dueMonth, dueDay);
    if (dueDate.getDate() !== dueDay) {
      // If dueDay is invalid (e.g., Apr 31), set to last day of month
      dueDate = new Date(dueYear, dueMonth + 1, 0); // Last day of dueMonth
    }
  
    return dueDate; // Return Date object instead of formatted string
};

// Format Date to MM/DD/YYYY or YYYY-MM-DD
const formatDate = (date, format = 'MM/DD/YYYY') => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    if (format === 'YYYY-MM-DD') {
      return `${year}-${month}-${day}`;
    }
    return `${month}/${day}/${year}`;
};

const Bills = ({ apartmentId }) => {
  const [bills, setBills] = useState([]); // Initialize as empty array
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newBill, setNewBill] = useState({
    id: 0,
    description: '',
    link: '',
    login: '',
    password: '',
    currency: 'USD',
    amount: '',
    dueDay: '10' // Default to 10th of the month
  });

  // Toggle password visibility
  const togglePasswordVisibility = (billId) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [billId]: !prev[billId]
    }));
  };

  // Handle form input changes
  const handleChange = (name, value) => {
    setNewBill(prev => ({ ...prev, [name]: value }));
  };

  // Validate form inputs
  const validateForm = () => {
    if (!newBill.description.trim()) return 'Description is required.';
    if (!newBill.link.trim()) return 'Link is required.';
    if (!/^(https?:\/\/)/i.test(newBill.link)) return 'Link must be a valid URL (e.g., https://example.com).';
    if (!newBill.login.trim()) return 'Login is required.';
    if (!newBill.password.trim()) return 'Password is required.';
    if (!newBill.currency) return 'Currency is required.';
    if (!newBill.amount || isNaN(newBill.amount) || Number(newBill.amount) < 0) return 'Amount must be a non-negative number.';
    if (!newBill.dueDay || isNaN(newBill.dueDay) || Number(newBill.dueDay) < 1 || Number(newBill.dueDay) > 31) {
      return 'Due Day must be a number between 1 and 31.';
    }
    return null;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const billData = {
      ...newBill,
      id: isEditing ? newBill.id : bills.length + 1, // Simple ID increment
      amount: Number(newBill.amount), // Ensure amount is a number
      dueDay: Number(newBill.dueDay) // Ensure dueDay is a number
    };

    if (isEditing) {
      setBills(bills.map(bill => bill.id === billData.id ? billData : bill));
      alert('Bill updated successfully');
    } else {
      setBills([...bills, billData]);
      alert('Bill added successfully');
    }

    resetForm();
    setOpenDialog(false);
  };

  // Handle edit
  const handleEdit = (bill) => {
    setIsEditing(true);
    setNewBill(bill);
    setOpenDialog(true);
  };

  // Handle delete
  const handleDelete = (billId) => {
    if (window.confirm('Are you sure you want to delete this bill?')) {
      setBills(bills.filter(bill => bill.id !== billId));
      alert('Bill deleted successfully');
    }
  };

  // Reset form
  const resetForm = () => {
    setNewBill({
      id: 0,
      description: '',
      link: '',
      login: '',
      password: '',
      currency: 'USD',
      amount: '',
      dueDay: '10'
    });
    setIsEditing(false);
    setError(null);
  };

  // Handle dialog open
  const handleOpenDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    resetForm();
    setOpenDialog(false);
  };

  // Handle PDF download
  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Bills for Apartment ${apartmentId}`, 14, 20);

    // Use Times font to support special characters like zł and ₹
    doc.setFont('times', 'normal');
    const tableColumn = ['Description', 'Link', 'Login', 'Password', 'Amount', 'Payment Due Date'];
    const tableRows = bills.map(bill => [
      bill.description,
      bill.link,
      bill.login,
      bill.password,
      `${currencySymbols[bill.currency]}${bill.amount.toFixed(2)} ${bill.currency}`,
      formatDate(getClosestDueDate(bill.dueDay), 'YYYY-MM-DD')
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 10, font: 'times' },
      headStyles: { fillColor: [66, 66, 66] },
      alternateRowStyles: { fillColor: [240, 240, 240] }
    });

    doc.save(`bills_apartment_${apartmentId}.pdf`);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Bills
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell>Link</TableCell>
              <TableCell>Login</TableCell>
              <TableCell>Password</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Payment Due Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bills.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No bills available.</TableCell>
              </TableRow>
            ) : (
              bills.map((bill) => (
                <TableRow key={bill.id}>
                  <TableCell>{bill.description}</TableCell>
                  <TableCell>
                    <Link href={bill.link} target="_blank" rel="noopener">
                      {bill.description || bill.link}
                    </Link>
                  </TableCell>
                  <TableCell>{bill.login}</TableCell>
                  <TableCell>
                    {visiblePasswords[bill.id] ? bill.password : '••••••••'}
                    <IconButton
                      onClick={() => togglePasswordVisibility(bill.id)}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      {visiblePasswords[bill.id] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>{currencySymbols[bill.currency]}{bill.amount.toFixed(2)} {bill.currency}</TableCell>
                  <TableCell>{formatDate(getClosestDueDate(bill.dueDay))}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(bill)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(bill.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <IconButton
          onClick={handleDownloadPDF}
          disabled={bills.length === 0}
          color="primary"
          title="Download as PDF"
        >
          <PictureAsPdfIcon />
        </IconButton>
        <CSVLink
          data={bills.map(bill => ({
            ...bill,
            paymentDueDate: formatDate(getClosestDueDate(bill.dueDay), 'YYYY-MM-DD')
          }))}
          headers={csvHeaders}
          filename={`bills_apartment_${apartmentId}.csv`}
          style={{ textDecoration: 'none' }}
        >
          <IconButton
            disabled={bills.length === 0}
            color="primary"
            title="Download as CSV"
          >
            <DescriptionIcon />
          </IconButton>
        </CSVLink>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add New Bill
        </Button>
      </Box>

      {/* Dialog for Add/Edit Bill */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Bill' : 'Add New Bill'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <form onSubmit={handleSubmit}>
            <Grid2 container spacing={3}>
              <Grid2 item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={newBill.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                  required
                />
              </Grid2>
              <Grid2 item xs={12} sm={6}>
                <TextField
                  label="Link"
                  name="link"
                  value={newBill.link}
                  onChange={(e) => handleChange('link', e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  type="url"
                />
              </Grid2>
              <Grid2 item xs={12} sm={6}>
                <TextField
                  label="Login"
                  name="login"
                  value={newBill.login}
                  onChange={(e) => handleChange('login', e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                />
              </Grid2>
              <Grid2 item xs={12} sm={6}>
                <TextField
                  label="Password"
                  name="password"
                  value={newBill.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  type="password"
                />
              </Grid2>
              <Grid2 item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel id="currency-label">Currency</InputLabel>
                  <Select
                    labelId="currency-label"
                    name="currency"
                    value={newBill.currency || 'USD'}
                    onChange={(e) => handleChange('currency', e.target.value)}
                    label="Currency"
                  >
                    {currencyOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 item xs={12} sm={6}>
                <TextField
                  label="Amount"
                  name="amount"
                  value={newBill.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  type="number"
                  InputProps={{ inputProps: { min: 0, step: '0.01' } }}
                />
              </Grid2>
              <Grid2 item xs={12} sm={6}>
                <TextField
                  label="Due Day of Month"
                  name="dueDay"
                  value={newBill.dueDay}
                  onChange={(e) => handleChange('dueDay', e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  type="number"
                  InputProps={{ inputProps: { min: 1, max: 31 } }}
                  helperText="Enter the day of the month (1–31) when the bill is due"
                />
              </Grid2>
            </Grid2>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="secondary">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {isEditing ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Bills;